import mixpanel from "mixpanel-browser";
import {
  initLifecycle,
  promoteByEvent,
  promoteStage,
  getCurrentStage,
  USER_STAGES,
} from "./user-lifecycle";
export { USER_STAGES, getCurrentStage, promoteStage } from "./user-lifecycle";
export type { UserStage } from "./user-lifecycle";

// ---------------------------------------------------------------------------
// Site type — one of: main | dinner | sponsor | conference
// ---------------------------------------------------------------------------

const SITE_TYPE = "conference" as const;

// ---------------------------------------------------------------------------
// Domain helpers
// ---------------------------------------------------------------------------

function getDomain(): string {
  if (typeof window === "undefined") return "";
  return window.location.hostname;
}

function getRootDomain(): string {
  const hostname = getDomain();
  if (!hostname) return "";
  const parts = hostname.split(".");
  // Handle cases like "localhost", "country.club", "sponsor.country.club"
  if (parts.length <= 2) return hostname;
  return parts.slice(-2).join(".");
}

// ---------------------------------------------------------------------------
// UTM helpers
// ---------------------------------------------------------------------------

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

function getUTMParams(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) utm[key] = val;
  }
  return utm;
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

let initialized = false;

export function initAnalytics(): void {
  if (initialized) return;
  if (typeof window === "undefined") return;

  const token = process.env.NEXT_PUBLIC_MIXPANEL_PROJECT_TOKEN;
  if (!token) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "[Analytics] NEXT_PUBLIC_MIXPANEL_PROJECT_TOKEN is not set. Mixpanel disabled.",
      );
    }
    return;
  }

  const appEnv = process.env.NEXT_PUBLIC_APP_ENV || "development";

  mixpanel.init(token, {
    persistence: "cookie",
    cross_subdomain_cookie: true,
    secure_cookie: true,
    ignore_dnt: true,
    debug: appEnv === "development",
    track_pageview: true,
    ip: true,
    autocapture: true,
    record_sessions_percent: 100,
    api_host: "/e",
    api_routes: {
      track: "t/",
      engage: "g/",
      groups: "gr/",
      record: "r/",
      flags: "f/",
    } as Record<string, string>,
  });

  // Register super properties — attached to EVERY event automatically
  const utmParams = getUTMParams();
  mixpanel.register({
    app_env: appEnv,
    domain: getDomain(),
    root_domain: getRootDomain(),
    site_type: SITE_TYPE,
    ...utmParams,
  });

  // First-touch UTM — set only once, never overwritten
  if (utmParams.utm_source) {
    mixpanel.register_once({ first_touch_utm_source: utmParams.utm_source });
  }

  // Persist UTM params to the user profile (for identified & anonymous)
  if (Object.keys(utmParams).length > 0) {
    mixpanel.people.set(utmParams);
    mixpanel.people.set_once({ first_touch_utm_source: utmParams.utm_source });
  }

  initialized = true;

  // Initialise user lifecycle stage tracking
  initLifecycle();
}

export function isInitialized(): boolean {
  return initialized;
}

// ---------------------------------------------------------------------------
// Identity: Identify user after successful auth
// ---------------------------------------------------------------------------

export function identifyUser(user: {
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  linkedInUrl?: string;
}): void {
  if (!initialized) return;
  const email = user.emailAddress || "";
  if (email) {
    mixpanel.identify(email);
  }
  // Having identity means at least "identified_lead"
  promoteStage(USER_STAGES.IDENTIFIED_LEAD);

  // Grab latest UTM params from super properties so they persist on profile
  const utmProfileProps: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const val = mixpanel.get_property(key);
    if (val) utmProfileProps[key] = val;
  }
  const firstTouch = mixpanel.get_property("first_touch_utm_source");

  mixpanel.people.set({
    $first_name: user.firstName || "",
    $last_name: user.lastName || "",
    $email: email,
    linkedin_url: user.linkedInUrl || "",
    user_stage: getCurrentStage(),
    domain: getDomain(),
    root_domain: getRootDomain(),
    site_type: SITE_TYPE,
    ...utmProfileProps,
  });

  if (firstTouch) {
    mixpanel.people.set_once({ first_touch_utm_source: firstTouch });
  }
}

// ---------------------------------------------------------------------------
// Core track wrapper — ensures domain props on every call
// ---------------------------------------------------------------------------

function track(eventName: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;

  // Auto-promote user stage based on the event being tracked
  const stage = promoteByEvent(eventName);

  mixpanel.track(eventName, {
    user_stage: stage,
    domain: getDomain(),
    root_domain: getRootDomain(),
    site_type: SITE_TYPE,
    page_url: typeof window !== "undefined" ? window.location.href : "",
    page_title: typeof document !== "undefined" ? document.title : "",
    referrer: typeof document !== "undefined" ? document.referrer : "",
    ...properties,
  });
}

// ---------------------------------------------------------------------------
// Event: Page Viewed
// ---------------------------------------------------------------------------

export function trackPageViewed(pageName: string, pagePath: string): void {
  // Visiting apply-member page signals intent
  if (pagePath === "/apply-member") {
    promoteStage(USER_STAGES.INTENT_USER);
  }

  track("Page Viewed", {
    page_name: pageName,
    page_path: pagePath,
  });
}

// ---------------------------------------------------------------------------
// Event: Section Viewed
// ---------------------------------------------------------------------------

export function trackSectionViewed(
  sectionName: string,
  sectionId: string,
): void {
  track("Section Viewed", {
    section_name: sectionName,
    section_id: sectionId,
  });
}

// ---------------------------------------------------------------------------
// Event: CTA Clicked
// ---------------------------------------------------------------------------

export function trackCTAClicked(options: {
  cta_location: string;
  cta_type: "meeting" | "demo" | "waitlist" | "career";
  cta_text?: string;
  link_url?: string;
}): void {
  track("CTA Clicked", {
    cta_location: options.cta_location,
    cta_type: options.cta_type,
    cta_text: options.cta_text || "",
    link_url: options.link_url || "",
  });
}

// ---------------------------------------------------------------------------
// Event: Meeting Modal Opened
// ---------------------------------------------------------------------------

export function trackMeetingModalOpened(triggerLocation: string): void {
  track("Meeting Modal Opened", {
    trigger_location: triggerLocation,
  });
}

// ---------------------------------------------------------------------------
// Event: Nav Link Clicked
// ---------------------------------------------------------------------------

export function trackNavLinkClicked(
  linkLabel: string,
  linkUrl: string,
  isExternal: boolean,
  navType: "desktop" | "mobile",
): void {
  track("Nav Link Clicked", {
    link_label: linkLabel,
    link_url: linkUrl,
    is_external: isExternal,
    nav_type: navType,
  });
}

// ---------------------------------------------------------------------------
// Event: Outbound Link Clicked
// ---------------------------------------------------------------------------

export function trackOutboundLinkClicked(
  linkUrl: string,
  linkText: string,
  linkLocation: string,
): void {
  track("Outbound Link Clicked", {
    link_url: linkUrl,
    link_text: linkText,
    link_location: linkLocation,
  });
}

// ---------------------------------------------------------------------------
// Event: LinkedIn Auth Initiated (Apply Member page)
// ---------------------------------------------------------------------------

export function trackLinkedInAuthInitiated(): void {
  track("LinkedIn Auth Initiated");
}

// ---------------------------------------------------------------------------
// Event: Application Submitted (Apply Member success)
// ---------------------------------------------------------------------------

export function trackApplicationSubmitted(): void {
  track("Application Submitted");
}

// ---------------------------------------------------------------------------
// Event: Member Application Completed (LinkedIn callback waitlist success)
// ---------------------------------------------------------------------------

export function trackMemberApplicationCompleted(): void {
  track("Member Application Completed", {
    is_duplicate: false,
  });
}

// ---------------------------------------------------------------------------
// Event: Member Application Duplicate (user already in waitlist)
// ---------------------------------------------------------------------------

export function trackMemberApplicationDuplicate(): void {
  track("Member Application Completed", {
    is_duplicate: true,
  });
}

// ---------------------------------------------------------------------------
// Event: Testimonial LinkedIn Clicked
// ---------------------------------------------------------------------------

export function trackTestimonialLinkedInClicked(
  personName: string,
  personRole: string,
  personCompany: string,
): void {
  track("Testimonial LinkedIn Clicked", {
    person_name: personName,
    person_role: personRole,
    person_company: personCompany,
  });
}

// ---------------------------------------------------------------------------
// Event: Scroll To Top Clicked
// ---------------------------------------------------------------------------

export function trackScrollToTopClicked(): void {
  track("Scroll To Top Clicked");
}

// ---------------------------------------------------------------------------
// Event: Video Section Viewed (high-intent — user reached video)
// ---------------------------------------------------------------------------

export function trackVideoSectionViewed(): void {
  track("Video Section Viewed");
}

// ---------------------------------------------------------------------------
// Event: Meeting Booked (Cal.com booking successful)
// ---------------------------------------------------------------------------

export function trackMeetingBooked(data: {
  uid?: string;
  title?: string;
  start_time?: string;
  end_time?: string;
  status?: string;
  linkedin_url?: string;
}): void {
  track("Meeting Booked", data);
}
